import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosRequestHeaders,
} from "axios";

import { refreshTokenApi } from "@/api/auth";
import { store } from "@/store";
import { logout, refreshToken } from "@/store/slices/authSlice";
import { resetPosthog } from "@/services/posthog";

import { BASE_URL_V2, BASE_URL_STRAPI, BASE_URL_STRAPI_TOKEN } from "@/config/vars";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL_V2,
});

const strapiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL_STRAPI,
  headers: {
    Authorization: `Bearer ${BASE_URL_STRAPI_TOKEN}`,
    "Content-Type": "application/json",
  },
});

const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const { userData } = store.getState().auth;

  const excludedRoutes: string[] = [
    "/login",
    "/register",
    "/email-verification/resend-email",
    "/forgot-password",
    "/reset-password",
    "/organizations",
    "/organizations/search-names",
    "/jobs/public",
    "/countries",
    "/states",
    "/cities",
    "/newsletter",
    "/refreshToken",
    "/auth/linkedin",
    "/auth/linkedin/accessToken",
    "/auth/social-login",
    "/contact-us",
    "/subscription-plans",
    "/cities/search",
    "/cities/popular",
  ];
  const excludedPrefixes: string[] = ["/passport/"];
  const language = localStorage.getItem("language") || "en";
  (config.headers as AxiosRequestHeaders)["Accept-Language"] = language;

  const isExcluded =
    excludedRoutes.includes(config.url) || excludedPrefixes.some((p) => config.url?.startsWith(p));

  if (!isExcluded) {
    if (userData && userData.access_token) {
      (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${userData.access_token}`;
    }
  }

  return config;
};

const responseInterceptor = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const { userData } = store.getState().auth;

      if (userData && userData.refresh_token) {
        const data = await refreshTokenApi({
          refresh_token: userData.refresh_token,
        });

        store.dispatch(refreshToken(data.access_token));
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;

        return await axios(originalRequest);
      }
    } catch (refreshError) {
      store.dispatch(logout());
      resetPosthog();
      return await Promise.reject(refreshError);
    }
  }
  return await Promise.reject(error);
};

axiosInstance.interceptors.request.use(
  requestInterceptor,
  (error: AxiosError): Promise<any> => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  responseInterceptor,
);

export { axiosInstance, strapiClient };
